import { TextArea as CompassTextArea } from "@circleco/compass/components/TextArea";
import { allowedClassesFor } from "@circleco/compass/helpers/allowedClassesFor";
import { omit } from "lodash";
import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type ComponentPropsWithRef,
} from "react";
import ReactTextAreaAutosize from "react-textarea-autosize";

import { anchorClassNames } from "@/react/helpers/cssAnchorHelpers";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { useIsCircleV4FeatureEnabled } from "@/react/hooks/useIsCircleV4FeatureEnabled";

type CompassTextAreaProps = ComponentPropsWithRef<typeof CompassTextArea>;

export interface TextAreaProps extends ComponentPropsWithoutRef<"textarea"> {
  autoSize?: CompassTextAreaProps["autoSize"];
  className?: string;
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  ({ className = "", autoSize, ...rest }, ref) => {
    const isCircleV4 = useIsCircleV4FeatureEnabled(
      "circle_v4_form_elements_enabled",
    );

    if (!isCircleV4) {
      if (autoSize) {
        const autosizeProps = omit(rest, ["style"]);
        return (
          <ReactTextAreaAutosize
            ref={ref}
            className={classNames(
              "placeholder:text-light placeholder:opacity-100",
              className,
            )}
            {...autosizeProps}
          />
        );
      }

      return (
        <textarea
          ref={ref}
          className={classNames(
            anchorClassNames("form-control"),
            "focus:!outline-secondary placeholder:text-light placeholder:opacity-100 focus:!outline-1 focus:!outline-offset-0 disabled:cursor-not-allowed disabled:opacity-40",
            className,
          )}
          {...rest}
        />
      );
    }

    const compassProps: CompassTextAreaProps = {
      ...rest,
      ref,
      resize: autoSize ? "none" : "vertical",
      autoSize,
      labelProps: { required: rest.required },
    };

    return (
      <CompassTextArea
        {...compassProps}
        className={allowedClassesFor("TextArea", compassProps.className ?? "")}
      />
    );
  },
);

TextArea.displayName = "TextArea";
